"use client"

import React, { useState, useContext } from "react"
import {
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Box,
  Tooltip,
  IconButton,
} from "@mui/material"
import { DatePicker, DateTimePicker } from "@mui/x-date-pickers"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import { Info } from "@mui/icons-material"
import mainContext from "../../contexts/mainContext"
import { sendCustomNotificationController, scheduleNotificationController } from "../../Controllers/Notifications"
import "./SendNotification.css"
import { __roles } from "../../constants"

const SendNotification = ({ onSuccess }) => {
  const context = useContext(mainContext)
  const { setSnackBar } = context

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    roles: [],
    startDate: null,
    endDate: null,
    scheduledTime: null,
  })

  const [loading, setLoading] = useState(false)
  const [isScheduling, setIsScheduling] = useState(false)

  // Character limits
  const TITLE_LIMIT = 40
  const MESSAGE_LIMIT = 120

  const roleOptions = [
    { value: "All", label: "All Users" },
    { value: __roles.seeker, label: "Customers" },
    { value: __roles.houseKeeper, label: "Housekeepers" },
    { value: __roles.babySitter, label: "Babysitters" },
  ]

  const handleInputChange = (field, value) => {
    // Apply character limits
    if (field === 'title' && value.length > TITLE_LIMIT) {
      value = value.substring(0, TITLE_LIMIT)
    }
    if (field === 'description' && value.length > MESSAGE_LIMIT) {
      value = value.substring(0, MESSAGE_LIMIT)
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleRoleChange = (event) => {
    const value = event.target.value
    if (value.includes("All")) {
      setFormData((prev) => ({
        ...prev,
        roles: ["All"],
      }))
    } else {
      setFormData((prev) => ({
        ...prev,
        roles: value.filter((role) => role !== "All"),
      }))
    }
  }

  const handleStartDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      startDate: date,
      endDate: date ? new Date() : null,
    }))
  }

  const handleEndDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      endDate: date,
    }))
  }

  const handleScheduledTimeChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      scheduledTime: date,
    }))
  }

  const getCurrentDate = () => {
    return new Date()
  }

  const getMaxEndDate = () => {
    return getCurrentDate()
  }

  const getMinEndDate = () => {
    return formData.startDate || null
  }

  const getMinScheduledTime = () => {
    const now = new Date()
    return new Date(now.getTime() + 5 * 60000)
  }

  const validateForm = () => {
    if (!formData.title.trim()) {
      setSnackBar({
        status: true,
        text: "Please enter a notification title",
        severity: "error",
      })
      return false
    }

    if (!formData.description.trim()) {
      setSnackBar({
        status: true,
        text: "Please enter a notification message",
        severity: "error",
      })
      return false
    }

    if (formData.roles.length === 0) {
      setSnackBar({
        status: true,
        text: "Please select at least one user role",
        severity: "error",
      })
      return false
    }

    if (isScheduling && !formData.scheduledTime) {
      setSnackBar({
        status: true,
        text: "Please select a scheduled time",
        severity: "error",
      })
      return false
    }

    if (isScheduling && formData.scheduledTime && formData.scheduledTime <= new Date()) {
      setSnackBar({
        status: true,
        text: "Scheduled time must be in the future",
        severity: "error",
      })
      return false
    }

    return true
  }

  const handleSubmit = async (e, isScheduled = false) => {
    e.preventDefault()
    setIsScheduling(isScheduled)

    if (!validateForm()) {
      return
    }

    setLoading(true)

    try {
      const requestData = {
        title: formData.title.trim(),
        header: formData.title.trim(),
        description: formData.description.trim(),
        type: isScheduled ? "scheduled_notification" : "custom_notification",
      }

      if (formData.roles.includes("All")) {
        requestData.roles = [__roles.seeker, __roles.houseKeeper, __roles.babySitter]
      } else {
        requestData.roles = formData.roles
      }

      if (formData.startDate) {
        requestData.startDate = formData.startDate.toISOString()
      }
      if (formData.endDate) {
        requestData.endDate = formData.endDate.toISOString()
      }

      if (isScheduled && formData.scheduledTime) {
        requestData.scheduledTime = formData.scheduledTime.toISOString()
      }

      const response = isScheduled
        ? await scheduleNotificationController(requestData)
        : await sendCustomNotificationController(requestData)

      if (response.data.status === 200) {
        if (isScheduled) {
          setSnackBar({
            status: true,
            text: `Notification scheduled successfully for ${formData.scheduledTime.toLocaleString()}`,
            severity: "success",
          })
        } else {
          const { count, breakdown } = response.data.data
          let successMessage = `Notification sent to users`

          if (breakdown && Object.keys(breakdown).length > 0) {
            const breakdownText = Object.entries(breakdown)
              .map(([role, count]) => `${role}: ${count}`)
              .join(", ")
            successMessage += ` (${breakdownText})`
          }

          setSnackBar({
            status: true,
            text: successMessage,
            severity: "success",
          })
        }

        setFormData({
          title: "",
          description: "",
          roles: [],
          startDate: null,
          endDate: null,
          scheduledTime: null,
        })

        if (onSuccess) {
          onSuccess()
        }
      } else {
        setSnackBar({
          status: true,
          text: response.data.message || `Failed to ${isScheduled ? "schedule" : "send"} notification`,
          severity: "error",
        })
      }
    } catch (error) {
      setSnackBar({
        status: true,
        text: `An error occurred while ${isScheduled ? "scheduling" : "sending"} the notification`,
        severity: "error",
      })
    } finally {
      setLoading(false)
      setIsScheduling(false)
    }
  }

  return (
    <div className="send-notification-container">
      <div className="send-notification-card">
        <div className="send-notification-header">
          <p>Send targeted notifications to users based on their roles and registration dates</p>
        </div>

        <form onSubmit={handleSubmit} className="send-notification-form">
          <div className="form-row">
            <div className="form-group full-width">
              <TextField
                label="Notification Title"
                variant="outlined"
                fullWidth
                value={formData.title}
                onChange={(e) => handleInputChange("title", e.target.value)}
                placeholder="Enter notification title"
                className="notification-input"
                helperText={`${formData.title.length}/${TITLE_LIMIT} characters`}
                inputProps={{ maxLength: TITLE_LIMIT }}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <TextField
                label="Notification Message"
                variant="outlined"
                fullWidth
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                placeholder="Enter your notification message here..."
                className="notification-textarea"
                helperText={`${formData.description.length}/${MESSAGE_LIMIT} characters`}
                inputProps={{ maxLength: MESSAGE_LIMIT }}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group full-width">
              <FormControl fullWidth variant="outlined" className="role-select">
                <InputLabel>Target User Roles</InputLabel>
                <Select
                  multiple
                  value={formData.roles}
                  onChange={handleRoleChange}
                  label="Target User Roles"
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((value) => {
                        const option = roleOptions.find((opt) => opt.value === value)
                        return <Chip key={value} label={option?.label || value} size="small" className="role-chip" />
                      })}
                    </Box>
                  )}
                >
                  {roleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <div className="date-field-container">
                <div className="date-field-label">
                  Start Date (Optional)
                  <Tooltip
                    title="Filter users who registered on or after this date"
                    placement="top"
                    classes={{ tooltip: "tooltip-content" }}
                  >
                    <IconButton size="small" className="info-tooltip">
                      <Info fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={formData.startDate}
                    onChange={handleStartDateChange}
                    maxDate={getCurrentDate()}
                    renderInput={(params) => <TextField {...params} fullWidth className="date-picker" />}
                  />
                </LocalizationProvider>
              </div>
            </div>
            <div className="form-group">
              <div className="date-field-container">
                <div className="date-field-label">
                  End Date (Optional)
                  <Tooltip
                    title="Filter users who registered on or before this date"
                    placement="top"
                    classes={{ tooltip: "tooltip-content" }}
                  >
                    <IconButton size="small" className="info-tooltip">
                      <Info fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={formData.endDate}
                    onChange={handleEndDateChange}
                    minDate={getMinEndDate()}
                    maxDate={getMaxEndDate()}
                    disabled={!formData.startDate}
                    renderInput={(params) => <TextField {...params} fullWidth className="date-picker" />}
                  />
                </LocalizationProvider>
              </div>
            </div>
          </div>

          <div className="schedule-section">
            <h4>Schedule Notification (Optional)</h4>
            <div className="form-row">
              <div className="form-group full-width">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DateTimePicker
                    label="Scheduled Time"
                    value={formData.scheduledTime}
                    onChange={handleScheduledTimeChange}
                    minDateTime={getMinScheduledTime()}
                    renderInput={(params) => <TextField {...params} fullWidth className="datetime-picker" />}
                  />
                </LocalizationProvider>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <Button
              type="submit"
              variant="contained"
              disabled={loading}
              className="send-notification-btn"
              onClick={(e) => handleSubmit(e, false)}
            >
              {loading && !isScheduling ? "Sending..." : "Send Now"}
            </Button>
            <Button
              variant="outlined"
              disabled={loading || !formData.scheduledTime}
              className="schedule-notification-btn"
              onClick={(e) => handleSubmit(e, true)}
            >
              {loading && isScheduling ? "Scheduling..." : "Schedule Notification"}
            </Button>
          </div>
        </form>

        <div className="notification-info">
          <h4>Information:</h4>
          <div className="info-list">
            <div className="info-item">Select "All Users" to send to everyone, or choose specific roles</div>
            <div className="info-item">Date filters are optional - leave empty to target all users of selected roles</div>
            <div className="info-item">Start/End dates filter users based on their registration date</div>
            <div className="info-item">To schedule a notification, select a future date and time, then click "Schedule Notification"</div>
            <div className="info-item">The notification will be sent as a push notification and saved for in-app display</div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SendNotification